import { fetchService } from './fetchService';
import { ExploreTopicResponse, HowToCardsResponse, KBPageContent, KBTreeNode, KnowledgeHubSearchResponse, Page, PopularDocumentsResponse, RecentlyViewedResponse, SearchResult, TopicCardsResponse } from './types';

export const fetchDocsNavigation = async (): Promise<Page[]> => {
  try {
    return await fetchService<Page[]>('fetch-confluence/navigation', {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch docs navigation.');
  }
};

export const fetchKnowledgeHubTree = async (): Promise<KBTreeNode[]> => {
  try {
    return await fetchService<KBTreeNode[]>('knowledge-hub/tree', {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch knowledge hub hierarchy.');
  }
};

export const triggerKnowledgeHubSync = async (): Promise<{ status: string; task_arn?: string }> => {
  try {
    return await fetchService<{ status: string; task_arn?: string }>('internal/sync', {
      method: 'POST',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to trigger sync.');
  }
};

export interface SyncStatus {
  last_synced_at: string | null;
  last_run_status: string | null;
  pages_seen: number | null;
}

export const fetchSyncStatus = async (): Promise<SyncStatus> => {
  try {
    return await fetchService<SyncStatus>('internal/sync/status', {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch sync status.');
  }
};

export const fetchKnowledgeHubPage = async (pageId: string): Promise<KBPageContent> => {
  try {
    return await fetchService<KBPageContent>(`knowledge-hub/pages/${pageId}`, {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch page content.');
  }
};

export const searchDocs = async (searchInput: string): Promise<SearchResult[]> => {
  try {
    return await fetchService<SearchResult[]>('fetch-confluence/search', {
      method: 'POST',
      body: { search_query: searchInput },
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Search failed.');
  }
};

export const searchKnowledgeHub = async (query: string, limit = 20): Promise<KnowledgeHubSearchResponse> => {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  try {
    return await fetchService<KnowledgeHubSearchResponse>(`knowledge-hub/search?${params.toString()}`, {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Search failed.');
  }
};

export const fetchConfluenceContent = async (pageId: string): Promise<{ title: string; content: string }> => {
  try {
    return await fetchService<{ title: string; content: string }>(`fetch-confluence/fetch-confluence-content/${pageId}`, {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch content.');
  }
};

export const fetchTopicCards = async (): Promise<TopicCardsResponse> => {
  try {
    return await fetchService<TopicCardsResponse>('knowledge-hub/topic-cards', {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch topic cards.');
  }
};

export const fetchHowToCards = async (): Promise<HowToCardsResponse> => {
  try {
    return await fetchService<HowToCardsResponse>('knowledge-hub/how-to-cards', {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch how-to cards.');
  }
};

export const fetchExploreTopic = async (topicId: string): Promise<ExploreTopicResponse> => {
  try {
    return await fetchService<ExploreTopicResponse>(`knowledge-hub/explore-topics/${topicId}`, {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch Explore topic details.');
  }
};

export const fetchPopularDocuments = async (): Promise<PopularDocumentsResponse> => {
  try {
    return await fetchService<PopularDocumentsResponse>('knowledge-hub/popular-documents', {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch popular documents.');
  }
};

export const recordDocumentView = async (pageId: string): Promise<void> => {
  try {
    await fetchService<unknown>(`knowledge-hub/documents/${pageId}/view`, {
      method: 'POST',
    });
  } catch {
    // View tracking is best-effort; never surface an error to the reader.
  }
};

export const fetchRecentlyViewed = async (): Promise<RecentlyViewedResponse> => {
  try {
    return await fetchService<RecentlyViewedResponse>('knowledge-hub/recently-viewed', {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch recently viewed documents.');
  }
};
